import { Avatar, Button, Input, Select, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { Link, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import userImage from "../../../../assets/images/empty_user.jpg";
import CustomTable from "../../../../components/ui/CustomTable";
import { useGetApprovalMatrixListQuery } from "../../../../features/approvalMatrix/approvalMatrixApi";
import { useGetExpenseUnitsQuery } from "../../../../features/org-setting/expenseUnit/expenseUnitApi";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import { ApprovalMatrixData } from "../../../../types/approvalMatrix";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import { addKeyInArray, selectArrayOption } from "../../../../utils/utils";

export default function BusinessUnitMatrixList() {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleFilter,
    handleSearch,
    handlePagination,
  } = useTable<{ business_unit_id?: number }>();

  const { data, isLoading, isFetching } = useGetApprovalMatrixListQuery({
    params: { level: "BUSINESS_UNIT", page, limit, search: keyword, ...filter },
  });

  const { data: businessUnitData } = useGetExpenseUnitsQuery({
    params: {
      skip: undefined,
      limit: 0,
      business_id: user?.data?.company_id,
    },
  });

  const leaveSettingsTabList = [
    {
      label: "Business Unit/SBU",
      link: `/admin/approval-matrix/vendor-management`,
    },
    {
      label: "Department",
      link: `/admin/approval-matrix/vendor-management/department`,
    },
  ];

  const columns: ColumnsType<ApprovalMatrixData> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },

    {
      title: <div className="text-nowrap">SBU/Business Unit</div>,
      dataIndex: "name",
      render: (val) => {
        return val ? capitalize(val) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap text-center w-full">Approvers</div>,
      align: "center",
      dataIndex: "approval_matrices",
      render: (record) => {
        return (
          <>
            <Avatar.Group size={28}>
              {record?.map((info: any) => (
                <Tooltip
                  openClassName="!text-xs"
                  title={info?.user?.username}
                  key={info?.user?.id}
                >
                  <Avatar
                    src={info?.user?.photo || userImage}
                    alt="organization_image"
                  />
                </Tooltip>
              ))}
            </Avatar.Group>
          </>
        );
      },
    },
    {
      title: <div className="text-nowrap">Update at</div>,
      dataIndex: "updated_at",
      align: "center",
      render: (val) => {
        return formatDate(val) || "N/A";
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val: number, record: any) => {
        return (
          <div className="flex justify-center items-center gap-2">
            <Button
              className="action-button"
              size="small"
              icon={<EditIcon />}
              onClick={() =>
                navigate(`/admin/approval-matrix/vendor-management/edit/${val}`)
              }
            />
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
              onClick={() =>
                navigate(`/admin/approval-matrix/vendor-management/view/${val}`)
              }
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SidebarLayout title={`Approval Matrix`} tabList={leaveSettingsTabList}>
        <CustomTable
          loading={isLoading || isFetching}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.count}
          dataSource={addKeyInArray(data?.data?.businessUnitApprovers || [])}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search by Name"
                style={{ width: 215 }}
                allowClear
                onChange={handleSearch}
              />

              <Select
                placeholder="SBU"
                showSearch
                options={selectArrayOption(
                  businessUnitData?.data.businessUnits || [],
                  "name",
                  "id",
                )}
                // value={filter?.active ?? undefined}
                onChange={(value) => handleFilter("business_unit_id", value)}
                className="!min-w-[110px]"
                suffixIcon={<SelectDownIcon />}
                allowClear
                optionFilterProp="label"
              />

              <Link to="/admin/approval-matrix/vendor-management/add">
                <Button
                  type="primary"
                  className="middle-button"
                  icon={<AddIcon />}
                >
                  Add Matrix
                </Button>
              </Link>
            </>
          }
        />
      </SidebarLayout>
    </>
  );
}
